import React, { useState, useEffect } from "react";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import Checkbox from "@material-ui/core/Checkbox";

import Page_Loader from "../../assets/vizr_images/page_loader.gif";
import AlertDrawer from "./alertDrawer";
import "./oneDriveInspectContentsStyles.scss";
import { isEmpty, isNull } from "lodash";
import {
  FILE_REVOKE_ACCESS,
  isRevokeAccessEnabled,
  NTFS,
  SET_FILE_REVOKE_LIST,
  SET_INSPECT_CURRENT_PAGE,
  SET_INSPECT_PAGE_SIZE,
  SET_LOADER,
} from "../../redux/constants/constants";
import {
  renderSensitivityValue,
  isCloudFileSystem,
  categoryIcons,
  hasFileAccessRevokeFeature,
  renderRevokePopup,
} from "../common/utilities";
import { POP_UP_LOCATION_KEY } from "../../features/Data Tag/DataTagConstants";

export default function InspectContentTable({
  allFilesTableData,
  loader,
  totalCount,
  sourceName,
  changeSource,
  loc,
  node,
  viewTab,
}) {
  const cloudFileSystem = isCloudFileSystem(sourceName) || sourceName === NTFS;
  const fileAccessRevoke = hasFileAccessRevokeFeature(sourceName);
  const isPublicFiles = viewTab === "publicFiles";
  const dispatch = useDispatch();
  const currentPage = useSelector(
    (state) => state.InspectContentReducer.currentPage
  );
  const pageSize = useSelector((state) => state.InspectContentReducer.pageSize);
  const filters = useSelector((state) => state.InspectContentReducer.filters);
  const dataMapFilePopUp = useSelector(
    (state) => state.dataMapReducer.dataMapFilePopUp
  );
  const fileRevokeList = useSelector(
    (state) => state.dataMapReducer.fileRevokeList
  );

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [rowData, setRowData] = useState([]);
  const [revokeDetails, setRevokeDetails] = useState(null);
  const [revokePopup, setRevokePopup] = useState(false);

  useEffect(() => {
    if (allFilesTableData.apiSuccess) {
      dispatch({ type: SET_LOADER, payload: true });
      setRowData(allFilesTableData.data);
      setPaginationModel((prevState) => ({
        ...prevState,
        page: currentPage,
        pageSize: pageSize,
      }));
      dispatch({ type: SET_LOADER, payload: false });
    }
  }, [allFilesTableData, currentPage, pageSize]);

  useEffect(() => {
    dispatch({ type: SET_INSPECT_CURRENT_PAGE, payload: paginationModel.page });
    dispatch({
      type: SET_INSPECT_PAGE_SIZE,
      payload: paginationModel.pageSize,
    });
  }, [paginationModel]);

  useEffect(() => {
    dispatch({ type: SET_INSPECT_CURRENT_PAGE, payload: 0 });
    dispatch({ type: SET_INSPECT_PAGE_SIZE, payload: 20 });
    setPaginationModel({ page: 0, pageSize: 20 });
  }, []);

  const renderNoDataMessage = () => {
    if (!loader) {
      return cloudFileSystem
        ? "No Files for this node"
        : "No Fields for this node";
    }
    return null;
  };

  const handlePaginationChange = (params) => {
    const newPagination = {
      pageSize: params.pageSize,
      page: params.page,
    };
    if (paginationModel.pageSize !== newPagination.pageSize) {
      newPagination.page = 0;
    }
    setPaginationModel(newPagination);
  };

  const renderNull = () => <>-</>;

  const renderIcons = (data) => {
    if (!isEmpty(data) && data !== "NO_SENSITIVITY") {
      const categoryName = data?.trim().toUpperCase();
      const icon = categoryIcons[categoryName];
      return (
        <img
          src={icon}
          alt={categoryName}
          title={categoryName}
          className="oneDriveIconImage"
        />
      );
    }
    return <div>-</div>;
  };

  const imageCount = (count) => {
    return count > 0 ? (
      <div className="circle oneDrive">
        <span className="circleText" title={count}>{`+${count}`}</span>
      </div>
    ) : (
      ""
    );
  };


  const renderNoData = (params) => (isNull(params.value) ? renderNull() : params.value);

  const handleFileRevokeCheckbox = (event, fileId, isDisabled) => {
    if (isDisabled) return;

    const updatedList = event.target.checked
      ? [...fileRevokeList, fileId]
      : fileRevokeList.filter((id) => id !== fileId);

    dispatch({ type: SET_FILE_REVOKE_LIST, payload: updatedList });
  };

  const handleRevokePopUp = (e, fileId, node, fileName) => {
    e.stopPropagation();
    setRevokeDetails({fileId, node, fileName})
    setRevokePopup(true);
  }

  const handleRevokeConfirm = (isDisabled) => {
    if (!isDisabled) {
      dispatch({
        type: FILE_REVOKE_ACCESS,
        payload: {
          dataSourceId: revokeDetails?.node,
          data: [revokeDetails?.fileId],
        },
      });
    }
    setRevokePopup(false);
  };

  const renderFileCellComp = (params) => {
    const fileId = params.row["Contents.sensitivityId"];
    const fileAuditTrail = !params.row["Contents.isAccessRevoked"];

    if (fileAccessRevoke && isPublicFiles && isRevokeAccessEnabled) {
      return (
        <div className="fileCheckboxHeader">
          <Checkbox
            checked={fileRevokeList.includes(fileId)}
            onChange={(event) =>
              handleFileRevokeCheckbox(event, fileId, fileAuditTrail)
            }
            onClick={(e) => e.stopPropagation()}
            color="primary"
            disabled={fileAuditTrail}
          />

          <div className="fileCheckboxHeader">
            <div className="inspect-file-header fileRevoke">
              <div className="inspect-file-fieldName" title={params.value}>
                {params.value}
              </div>
            </div>
            <div>
              <img
                src="/dataSourceIcons/fileRevoke.svg"
                alt="revoke access icon"
                className={`publicRevokeAccess ${fileAuditTrail ? "disabled" : ""}`}
                onClick={(e) => {
                  handleRevokePopUp(e, fileId, node, params.value);
                }}
                title={
                  fileAuditTrail
                    ? "Revoke disabled"
                    : "Revoke Access"
                }
              />
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="inspect-file-header">
          <div className="inspect-file-fieldName" title={params.value}>
            {params.value}
          </div>
        </div>
      );
    }
  };


  const renderFileCell = (params) => {
    return isNull(params.value) ? renderNull() : renderFileCellComp(params);
  };

  const renderCategory = (params) => {
    return isNull(params.value) ? (
      renderNull()
    ) : (
      <div className="oneDriveCategoryContainer">
        <div className="oneDriveCategoryIcons">{renderIcons(params.value)}</div>
      </div>
    );
  };

  const renderSensitivityCell = (params) => {
    return isNull(params.value) ? (
      renderNull()
    ) : (
      <div className={`inspect-file-severity ${params.value}`}>
        {renderSensitivityValue(params.value)}
      </div>
    );
  };

  const renderLastDLPScanTimeCell = (params) => {
    return isNull(params.value) ? (
      renderNull()
    ) : (
      <div>
        <div>{params.value}</div>
      </div>
    );
  };

  const customNoRowsOverlay = () => (
    <GridOverlay>
      <div>No Results</div>
    </GridOverlay>
  );

const renderFileHeader = () => {
  if (fileAccessRevoke && isPublicFiles && isRevokeAccessEnabled) {
    const enabledRows = rowData.filter(
      (row) => row["Contents.isAccessRevoked"] === true
    );
    const allEnabledIds = enabledRows.map(
      (row) => row["Contents.sensitivityId"]
    );

    return (
      <div className="fileCheckboxHeader mainHeader">
        <Checkbox
          checked={
            allEnabledIds.length > 0 &&
            fileRevokeList.length === allEnabledIds.length
          }
          indeterminate={
            fileRevokeList.length > 0 &&
            fileRevokeList.length < allEnabledIds.length
          }
          onChange={(event) => {
            const updatedList = event.target.checked ? allEnabledIds : [];
            dispatch({ type: SET_FILE_REVOKE_LIST, payload: updatedList });
          }}
          color="primary"
        />
        <div>Files</div>
      </div>
    );
  } else {
    return cloudFileSystem ? "Files" : "Fields";
  }
};


  const columns = [
    {
      field: "Contents.columnName",
      headerName: renderFileHeader(),
      flex: 0.8,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: renderFileCell,
    },
    {
      field: "Contents.category",
      headerName: "Sensitivity",
      flex: 0.5,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: renderSensitivityCell,
    },
    {
      field: "Contents.finalType",
      headerName: "Category",
      flex: 0.6,
      align: "left",
      headerAlign: "left",
      sortable: false,
      renderCell: renderCategory,
    },
    {
      field: cloudFileSystem ? "Contents.fileOwner" : "Contents.tableName",
      headerName: cloudFileSystem ? "Owner" : " Table Name",
      flex: 0.6,
      align: "left",
      headerAlign: "left",
      sortable: false,
      renderCell: renderNoData,
    },
    {
      field: "Contents.lastAccess",
      headerName: "Last Access",
      type: "number",
      flex: 0.5,
      headerAlign: "left",
      align: "center",
      sortable: false,
      hide: true,
      renderCell: renderNoData,
    },
    {
      field: "Contents.lastDLPScanTime",
      headerName: "Last Scan",
      type: "number",
      flex: 0.5,
      headerAlign: "left",
      align: "center",
      sortable: false,
      renderCell: renderLastDLPScanTimeCell,
    },
  ];

  const filteredColumns = !cloudFileSystem
    ? columns.filter((column) => column.hide !== true)
    : columns;

  const handleFilePopUp = (data) => {
    dispatch({ type: "OPEN_FILE_POP_UP", payload: data.row });
    dispatch({ type: POP_UP_LOCATION_KEY, payload: "dataMap" });
  };

  const handleRevokePopupClose = () => {
    setRevokePopup(false);
  }

  const renderSidePopup = () => (
    <AlertDrawer
      dataMapFilePopUp={dataMapFilePopUp}
      sourceName={sourceName}
      loc={"Data-Map-File-Pop-up"}
    />
  );

  const renderTable = () => {
    if (!isEmpty(rowData) || !isEmpty(filters)) {
      return (
        <div className="custom-data-grid-main">
          <DataGrid
            className="custom-data-grid"
            rows={rowData}
            rowCount={totalCount}
            columns={filteredColumns}
            getRowId={(row) => row["Contents.sensitivityId"]}
            rowHeight={90}
            components={{ NoRowsOverlay: customNoRowsOverlay }}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            showCellRightBorder={false}
            showColumnRightBorder={false}
            showCellVerticalBorder={true}
            disableColumnFilter={true}
            disableColumnSelector
            disableColumnMenu
            paginationMode="server"
            hideFooterSelectedRowCount={true}
            getRowSpacing={(params) => ({
              top: 10,
              bottom: params.isLastVisible ? 0 : 5,
            })}
            onRowClick={handleFilePopUp}
          />
          {renderSidePopup()}
        </div>
      );
    } else {
      return (
        <div>
          <p>{renderNoDataMessage()}</p>
        </div>
      );
    }
  };

  const renderContentTable = () => {
    if (loc === "fullScreen") {
      return loader && changeSource ? null : renderTable();
    } else {
      return (
        <>
          {loader && (
            <img
              src={Page_Loader}
              alt="loading"
              className="loader-data-source oneDrive-Table"
            />
          )}
          {renderTable()}
        </>
      );
    }
  };

  return (
    <>
      {renderContentTable()}
      {renderRevokePopup(
        revokePopup,
        revokeDetails,
        handleRevokePopupClose,
        handleRevokeConfirm,
        "dataMapTable"
      )}
    </>
  );
}
